import { Component, DestroyRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import { SplitComponent, SplitAreaComponent } from 'angular-split';
import { PDFDocumentProxy } from 'ng2-pdf-viewer';

import { EditTemplateFormTableHostComponent } from '../../components/edit-template-form-table-host/edit-template-form-table-host.component';
import { ExtendedPdfViewerComponent } from '../../components/pdf-viewer/extended-pdf-viewer/extended-pdf-viewer.component';
import { ProgressBarComponent } from '../../components/progress-bar/progress-bar.component';
import { PDFAnnotationData } from '../../interfaces/pdf-annotation-data.interface';
import { RawFormData } from '../../interfaces/pdf-viewer.interface';
import { EditTemplateService } from '../../services/edit-template/edit-template.service';
import { PdfViewerSrcManagerService } from '../../services/pdf-viewer-src-manager/pdf-viewer-src-manager.service';
import { ToastNotificationsManagerService } from '../../services/toast-notifications-manager/toast-notifications-manager.service';

@Component({
  selector: 'app-edit-template',
  templateUrl: './edit-template.component.html',
  styleUrls: ['./edit-template.component.scss'],
  imports: [
    ProgressBarComponent,
    SplitComponent,
    SplitAreaComponent,
    ExtendedPdfViewerComponent,
    EditTemplateFormTableHostComponent
],
})
export class EditTemplateComponent implements OnInit, OnDestroy {
  /* splitEl is the element that splits the pdf side and the document editing side */
  @ViewChild('splitEl') splitEl: SplitComponent;

  /* pdfViewer component will be populated withthe PDF once the PDF load is completed */
  @ViewChild('pdfViewer') pdfViewer: ExtendedPdfViewerComponent;

  /* isTemplateViewReady used as a main variable to show loader if the view isn't ready */
  isTemplateViewReady = false;
  loadingViewMessage = 'Please wait...';

  /* newTemplatePdfDocumentProxy can be used to access the PDF Documents input elements */
  newTemplatePdfDocumentProxy: PDFDocumentProxy | undefined = undefined;

  /**
   * jsonNewTemplateConfiguration
   * new template configuration should be added to the JSON
   * TODO: should eventually be an interface which defines the configuration JSON which is ready by the edit-document component not any
   */
  jsonNewTemplateConfiguration: any = {};
  stagingTemplateID: number;

  constructor(
    readonly editTemplateService: EditTemplateService,
    readonly pdfSrcService: PdfViewerSrcManagerService,
    private activatedRoute: ActivatedRoute,
    private toastNotif: ToastNotificationsManagerService,
    private destroyRef: DestroyRef,
  ) {
    // TODO: display load indicator and "Rendering document..." message while pdfLoadComplete is falsey.
    // TODO: once load is complete remove load indicator and all messages.
    // TODO: once load is complete start process to update JSON with input IDs.
    // TODO: temporarily display JSON in a temporary pre element  so that the UI can modify it.
    // TODO: delete f8802.pdf from assets... why is it still there?
  }

  /**
   * setTemplateBinaryForViewer() will try to assign the file in the service
   * to the pdf viewer if present or call a function to download binary
   * @returns void
   */
  setTemplateBinaryForViewer() {
    /* if statement prevents errors when .file doesn't exist as it can be undefined */
    if (this.editTemplateService.newDocumentTemplate.file) {
      this.loadingViewMessage = 'Generating...';
      this.pdfSrcService.setPdfSourceFile(this.editTemplateService.newDocumentTemplate.file);
      this.editTemplateService.file = this.editTemplateService.newDocumentTemplate.file;
      return;
    }
    this.getTemplateBinaryByID();
  }

  /**
   * getTemplateBinaryByID() is called to check the URL for template ID parameter
   * or fetch by the default hardcoded ID. Only positive numbers as ID or show error
   */
  getTemplateBinaryByID() {
    this.loadingViewMessage = 'Downloading template...';
    /* Hardcoded default templateID to fetch the template without any inputs detected */
    let templateID = 1774;
    this.activatedRoute.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params: Params) => {
        if (params.StagingTemplateID) templateID = Number(params.StagingTemplateID);
      });
    this.editTemplateService.newDocumentTemplate.StagingTemplateID = templateID;
    if (typeof templateID === 'number' && templateID >= 0)
      this.pdfSrcService.getPdfTemplateBinary(templateID);
    else {
      this.toastNotif.addNew({
        type: 'error',
        content: 'Provided template ID is invalid, it must be a positive number only',
      });
      this.isTemplateViewReady = true;
    }
  }

  /* bindToPdfSrcService() will watch for pdf source changes and perform designated actions */
  bindToPdfSrcService() {
    this.pdfSrcService.pdfSrc
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((src: Uint8Array | null) => {
        if (src instanceof Uint8Array) this.loadingViewMessage = 'Generating...';
      });
  }

  /**
   * parseNewTemplatePdfDocumentProxy()
   * identifies the anotations which might include input fields such as:
   * annotation.fieldType Tx "TEXT", Ch "DROPDOWN", Btn "CHECKBOX"/"RADIO"
   * @param viewerPdfProxy is the Document proxy data emitted by pdf viewer component
   */
  async parseNewTemplatePdfDocumentProxy(viewerPdfProxy: PDFDocumentProxy) {
    if (viewerPdfProxy) this.newTemplatePdfDocumentProxy = viewerPdfProxy;
    if (!this.newTemplatePdfDocumentProxy) return;

    const tempArrForAnnotations: RawFormData[] = [];
    for (let i = 1; i <= this.newTemplatePdfDocumentProxy.numPages; i++) {
      const page = await this.newTemplatePdfDocumentProxy.getPage(i);
      const pageAnnotations: PDFAnnotationData[] = await page.getAnnotations();
      pageAnnotations.forEach(annotation => {
        tempArrForAnnotations.push({
          ID: annotation.id,
          SUBTYPE: annotation.subtype,
          FIELDTYPE: annotation.fieldType,
          FIELDNAME: annotation.fieldName,
          DESCRIPTION: '',
          REQUIRED: false,
          HIDDEN: false,
          MAXCHARACTERS: undefined,
          BOFIELDNAME: undefined,
        });
      });
      // TODO: only "Widget" subtype is likely necessary but let's test this assumption
      // TODO: should fillable input fields be created here dynamically (with top, left, width, height, customCss, required etc.)?
    }
    this.editTemplateService.temporaryJson = this.editTemplateService.filteredTempJson =
      tempArrForAnnotations;
    this.isTemplateViewReady = true;
  }

  ngOnInit(): void {
    this.setTemplateBinaryForViewer();
    this.bindToPdfSrcService();
  }

  ngOnDestroy(): void {
    this.editTemplateService.clearStoredData();
  }
}
